export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  initialDelay = 100,
  maxDelay = 30000,
  timeoutMs?: number,
  options?: { maxAttempts?: number; shouldRetry?: (error: unknown) => boolean }
): Promise<T> {
  const startTime = Date.now();
  let attempt = 1;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      if (options?.shouldRetry && !options.shouldRetry(error)) {
        throw error;
      }

      if (options?.maxAttempts != null && attempt >= options.maxAttempts) {
        throw error;
      }

      if (timeoutMs && Date.now() - startTime > timeoutMs) {
        throw new Error(`Timeout: Operation failed after ${attempt} attempts (${timeoutMs}ms)`);
      }

      console.warn(`Retry attempt ${attempt} failed:`, error);
      const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
      await new Promise((resolve) => setTimeout(resolve, delay));
      attempt++;
    }
  }
}

export async function retryWithFixedInterval<T>(fn: () => Promise<T>, intervalMs = 2000, timeoutMs?: number): Promise<T> {
  const startTime = Date.now();
  let attempt = 1;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      if (timeoutMs && Date.now() - startTime > timeoutMs) {
        throw new Error(`Timeout: Operation failed after ${attempt} attempts (${timeoutMs}ms)`);
      }

      console.warn(`Retry attempt ${attempt} failed:`, error);
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      attempt++;
    }
  }
}
